import http from "../core/api/httpClient";
import { getOwnerAccessToken } from "../core/auth/ownerAccessToken";
import {
  OwnerDashboardResponse,
  OwnerKpi,
  OwnerTone,
  OwnerWorkloadAdviser,
} from "../types/owner.types";

interface OwnerKpisApiResponse {
  firm_id: string;
  kpis: Array<{
    label: string;
    value: string;
    delta: string;
    tone: string;
    trend: string;
    icon: string;
  }>;
}

interface OwnerCaseByStageApiResponse {
  firm_id: string;
  title: string;
  rows: Array<{
    label: string;
    count: number;
    width: number;
    color: string;
  }>;
  summary_cards: Array<{
    label: string;
    value: string;
    tone: string;
  }>;
}

interface OwnerAdvisorWorkloadApiResponse {
  firm_id: string;
  title: string;
  advisers: Array<{
    id: string;
    initials: string;
    avatar_tone: string;
    name: string;
    active_cases: number;
    overdue_text: string;
    overdue_tone: string;
    load_percent: number;
    badge_label: string;
    badge_tone: string;
    bar_tone: string;
  }>;
}

interface OwnerRecentCompletionsApiResponse {
  firm_id: string;
  title: string;
  items: Array<{
    id: string;
    dot_tone: string;
    text: string;
    time: string;
    badge_label: string;
    badge_tone: string;
  }>;
}

interface UserListItemApiResponse {
  id: string;
  role: string;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  is_active: boolean;
  created_at: string;
}

export interface OwnerFirmUser {
  id: string;
  name: string;
  username: string;
  role: string;
  email: string;
  phone: string | null;
  isActive: boolean;
  createdAt: string;
}

interface UserCreateApiResponse {
  id: string;
  role: string;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  is_active: boolean;
  created_at: string;
}

export interface CreateFirmUserPayload {
  role: "ASSISTANT" | "ADVISOR";
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isActive: boolean;
}

const normalizeTone = (value: string, fallback: OwnerTone): OwnerTone => {
  const normalized = value.trim().toLowerCase();
  const allowed: OwnerTone[] = ["blue", "green", "amber", "red", "purple", "teal", "gray"];
  return allowed.includes(normalized as OwnerTone) ? (normalized as OwnerTone) : fallback;
};

const normalizeTrend = (value: string): OwnerKpi["trend"] => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "danger") return "danger";
  if (normalized === "warn") return "warn";
  return "up";
};

const normalizeOverdueTone = (value: string): OwnerWorkloadAdviser["overdueTone"] => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "red") return "red";
  if (normalized === "amber") return "amber";
  return "green";
};

const normalizeBarTone = (value: string): OwnerWorkloadAdviser["barTone"] => {
  const normalized = value.trim().toLowerCase();
  return normalized === "green" ? "green" : "blue";
};

const normalizeRoleLabel = (value: string): string => {
  const normalized = value.trim().toUpperCase();
  if (normalized === "ADVISOR") return "Adviser";
  if (normalized === "ASSISTANT") return "Assistant";
  if (normalized === "CLIENT") return "Client";
  if (normalized === "OWNER") return "Owner";
  return normalized.charAt(0) + normalized.slice(1).toLowerCase();
};

const mapApiUserToOwnerFirmUser = (
  item: UserListItemApiResponse | UserCreateApiResponse,
): OwnerFirmUser => {
  const firstName = item.first_name.trim();
  const lastName = item.last_name.trim();
  const fullName = `${firstName} ${lastName}`.trim();
  return {
    id: item.id,
    name: fullName || item.username,
    username: item.username,
    role: normalizeRoleLabel(item.role),
    email: item.email,
    phone: item.phone,
    isActive: item.is_active,
    createdAt: item.created_at,
  };
};

const getOwnerAuthHeaders = (): Record<string, string> => {
  const token = getOwnerAccessToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
};

export const ownerService = {
  async getDashboard(
    ownerName?: string,
  ): Promise<OwnerDashboardResponse> {
    const authHeaders = getOwnerAuthHeaders();
    const [kpisRes, stageRes, workloadRes, completionsRes] = await Promise.all([
      http.get<OwnerKpisApiResponse>("/owner/kpis", {
        headers: authHeaders,
      }),
      http.get<OwnerCaseByStageApiResponse>("/owner/case-by-stage", {
        headers: authHeaders,
      }),
      http.get<OwnerAdvisorWorkloadApiResponse>("/owner/advisor-workload", {
        headers: authHeaders,
      }),
      http.get<OwnerRecentCompletionsApiResponse>("/owner/recent-completions", {
        params: { limit: 10 },
        headers: authHeaders,
      }),
    ]);

    const kpis = kpisRes.data.kpis.map((kpi) => ({
      label: kpi.label,
      value: kpi.value,
      delta: kpi.delta,
      tone: normalizeTone(kpi.tone, "blue"),
      trend: normalizeTrend(kpi.trend),
      icon: kpi.icon,
    }));

    const stageDistribution = {
      title: stageRes.data.title,
      rows: stageRes.data.rows,
      summaryCards: stageRes.data.summary_cards.map((card) => ({
        label: card.label,
        value: card.value,
        tone: normalizeTone(card.tone, "gray") as "gray" | "green" | "red",
      })),
    };

    const workload = {
      title: workloadRes.data.title,
      advisers: workloadRes.data.advisers.map((adviser) => ({
        id: adviser.id,
        initials: adviser.initials,
        avatarTone: normalizeTone(adviser.avatar_tone, "blue") as
          | "blue"
          | "green"
          | "amber"
          | "purple"
          | "teal"
          | "red",
        name: adviser.name,
        activeCases: adviser.active_cases,
        overdueText: adviser.overdue_text,
        overdueTone: normalizeOverdueTone(adviser.overdue_tone),
        loadPercent: adviser.load_percent,
        badgeLabel: adviser.badge_label,
        badgeTone: normalizeTone(adviser.badge_tone, "gray"),
        barTone: normalizeBarTone(adviser.bar_tone),
      })),
    };

    const completions = {
      title: completionsRes.data.title,
      items: completionsRes.data.items.map((item) => ({
        id: item.id,
        dotTone: normalizeTone(item.dot_tone, "gray"),
        text: item.text,
        time: item.time,
        badgeLabel: item.badge_label,
        badgeTone: normalizeTone(item.badge_tone, "gray"),
      })),
    };

    return {
      header: {
        title: ownerName ? `${ownerName} · Firm overview` : "Firm overview",
        subtitle: "KPI health, stage distribution, advisor load, and recent outcomes.",
      },
      kpis,
      stageDistribution,
      workload,
      completions,
    };
  },

  async getFirmUsers(firmId: string): Promise<OwnerFirmUser[]> {
    const limit = 200;
    let offset = 0;
    const allUsers: UserListItemApiResponse[] = [];

    while (true) {
      const usersRes = await http.get<UserListItemApiResponse[]>("/users", {
        params: {
          firm_id: firmId,
          limit,
          offset,
        },
        headers: getOwnerAuthHeaders(),
      });
      const batch = usersRes.data;
      allUsers.push(...batch);
      if (batch.length < limit) break;
      offset += batch.length;
    }

    return allUsers.map(mapApiUserToOwnerFirmUser);
  },

  async createFirmUser(
    firmId: string,
    payload: CreateFirmUserPayload,
  ): Promise<OwnerFirmUser> {
    const res = await http.post<UserCreateApiResponse>("/users", {
      firm_id: firmId,
      role: payload.role,
      username: payload.username.trim(),
      first_name: payload.firstName.trim(),
      last_name: payload.lastName.trim(),
      email: payload.email.trim(),
      phone: payload.phone.trim() || null,
      is_active: payload.isActive,
    }, {
      headers: getOwnerAuthHeaders(),
    });
    return mapApiUserToOwnerFirmUser(res.data);
  },
};
